/** Capture-a-keyboard-combo helper for the Settings → Keyboard
 *  remap UI. Takes a raw KeyboardEvent and returns a CommandShortcut
 *  that the registry can match — OR returns one of two sentinels
 *  (`null` = ignore, `'cancel'` = user pressed Escape and wants to
 *  abandon the recording).
 *
 *  Modifier-only presses are ignored so the user can hold Shift /
 *  Ctrl / Cmd / Alt while reaching for the actual key without the
 *  recorder firing on the modifier press itself. The recorder
 *  fires on the FIRST press of a non-modifier key (with whatever
 *  modifiers were already held).
 *
 *  Platform-aware `mod` vs `ctrl` mapping (mirrors how the
 *  registry's matcher interprets bindings):
 *    macOS:    Cmd pressed → `mod: true`; literal Ctrl → `ctrl: true`
 *    Win/Lin:  Ctrl pressed → `mod: true` (no literal-Ctrl variant
 *              recorded — `ctrl: true` is a Mac-only distinction). */

import type { CommandShortcut } from './command-registry';

/** Sentinel value distinct from `null` (= "keep listening") and from
 *  a captured shortcut. Returned when the user presses Escape during
 *  recording — the dialog uses this to exit edit mode without
 *  applying a change. */
export const CAPTURE_CANCELLED = Symbol('combo-recorder:cancelled');
export type CaptureResult = CommandShortcut | typeof CAPTURE_CANCELLED | null;

const MODIFIER_KEYS = new Set([
  'Shift',
  'Control',
  'Meta',
  'Alt',
  'AltGraph',
  'OS',
  // Dead keys can fire on macOS when the user presses Option +
  // accent — treat them as modifier-like and ignore so the recorder
  // doesn't capture "Dead" as a shortcut.
  'Dead',
]);

/** Inspect a keydown event during shortcut recording. Returns the
 *  captured shortcut (commit), the cancel sentinel (Escape pressed,
 *  abandon recording), or `null` (modifier-only press — keep
 *  listening). */
export function captureCombo(e: KeyboardEvent, isMac: boolean): CaptureResult {
  if (e.key === 'Escape') return CAPTURE_CANCELLED;
  if (MODIFIER_KEYS.has(e.key)) return null;

  const platformMod = isMac ? e.metaKey : e.ctrlKey;
  // On Mac, the registry distinguishes mod (Cmd) from ctrl
  // (literal Control). Recording Ctrl-only on Mac means literal
  // Ctrl. Recording Ctrl on Win/Linux means the platform mod
  // (already covered by `platformMod`).
  const literalCtrl = isMac && e.ctrlKey && !e.metaKey;

  const out: CommandShortcut = { key: e.key };
  // Preserve `code` for layout-independence — German QWERTZ users
  // pressing the physical Equal key see e.code === 'Equal' even
  // though e.key may differ. Captures stay portable across layouts.
  if (e.code) out.code = e.code;
  if (platformMod) out.mod = true;
  if (literalCtrl) out.ctrl = true;
  if (e.shiftKey) out.shift = true;
  if (e.altKey) out.alt = true;
  return out;
}

/** True when a capture result is a real CommandShortcut (not a
 *  sentinel). Type guard for the dialog's "did the user commit a
 *  combo?" branch. */
export function isCapturedShortcut(r: CaptureResult): r is CommandShortcut {
  return r !== null && r !== CAPTURE_CANCELLED;
}
